/**
 * Twinny X's plans as twinnyx.com sells them (`twinny-web-experience/src/data/site.ts`:
 * PLANS + PLAN_MATRIX). The web is the source: when its plans change, change them here.
 * Enterprise is not a plan of its own (sales), so it is not listed.
 *
 * Front-side reading only: the backend decides what each plan really allows (and Stripe's
 * prices what each costs). This decides what the panel shows enabled, what it tags with
 * the plan it asks for, and which plan an upgrade leads to.
 */
export type TwxPlanId = 'free' | 'starter' | 'growth' | 'scale';
export type TwxPaidPlan = Exclude<TwxPlanId, 'free'>;

/** Lowest first: a plan includes everything of the ones before it. */
export const TWX_PLAN_ORDER: TwxPlanId[] = ['free', 'starter', 'growth', 'scale'];
export const TWX_PAID_PLANS: TwxPaidPlan[] = ['starter', 'growth', 'scale'];

export const isTwxPlan = (value: unknown): value is TwxPlanId =>
    typeof value === 'string' && (TWX_PLAN_ORDER as string[]).includes(value);

export const planRank = (plan: TwxPlanId) => TWX_PLAN_ORDER.indexOf(plan);

/** The web's prices, EUR per month (Stripe's prices are the ones charged). */
export const TWX_PLAN_PRICE: Record<TwxPlanId, number> = {
    free: 0,
    starter: 29,
    growth: 99,
    scale: 249,
};

/**
 * What each plan includes, counted. `urls`: pages of the website the crawl reads (null =
 * no limit). `contacts`: AI-assisted visitors per month — the web's "visitantes asistidos",
 * the contacts the `mac_ai` meter counts.
 */
export const TWX_PLAN_LIMITS: Record<TwxPlanId, { contacts: number; websites: number; urls: number | null }> = {
    free: { contacts: 50, websites: 1, urls: 50 },
    starter: { contacts: 200, websites: 1, urls: null },
    growth: { contacts: 1000, websites: 3, urls: null },
    scale: { contacts: 3000, websites: 10, urls: null },
};

/**
 * twinny-api's ceiling for a website's `max_pages` (`WebDocumentService.MAX_PAGES_LIMIT`):
 * what a plan without a limit of pages holds in practice.
 */
export const TWX_MAX_PAGES = 5000;

/**
 * The pages a website of the plan holds in its memory at once: what its web_source's
 * `max_pages` must say (Free: 50, the paid plans: the backend's ceiling).
 */
export const planPagesOf = (plan: TwxPlanId) => TWX_PLAN_LIMITS[plan].urls ?? TWX_MAX_PAGES;

/** What a paid plan unlocks (Free has the rest), named after what the panel shows. */
export type TwxFeature =
    // Training
    | 'unlimitedUrls'
    | 'documents'
    | 'catalog'
    // Experience engine
    | 'voice'
    | 'proactive'
    | 'forms'
    | 'removeBranding'
    | 'winningExperiences'
    | 'audienceExperiences'
    // Traceability and analytics
    | 'audience'
    | 'journeys'
    | 'copies'
    | 'intent'
    | 'improvements'
    | 'export'
    // Labels and evaluation
    | 'labels'
    | 'evaluation'
    | 'evaluators'
    // Integrations
    | 'calendarSync'
    | 'notifyEmail'
    | 'leadEmails'
    | 'whatsapp'
    | 'crm'
    | 'integrations'
    | 'allIntegrations'
    // Campaigns
    | 'campaigns'
    | 'attribution'
    // Account
    | 'websites';

/** The lowest plan each feature asks for, as the web's comparison table says. */
export const TWX_FEATURE_PLAN: Record<TwxFeature, TwxPaidPlan> = {
    unlimitedUrls: 'starter',
    documents: 'starter',
    catalog: 'growth',

    voice: 'starter',
    proactive: 'starter',
    forms: 'starter',
    removeBranding: 'starter',
    winningExperiences: 'growth',
    audienceExperiences: 'scale',

    audience: 'starter',
    journeys: 'growth',
    copies: 'growth',
    intent: 'growth',
    improvements: 'growth',
    export: 'scale',

    labels: 'starter',
    evaluation: 'starter',
    evaluators: 'growth',

    calendarSync: 'starter',
    notifyEmail: 'growth',
    leadEmails: 'growth',
    whatsapp: 'growth',
    crm: 'growth',
    integrations: 'growth',
    allIntegrations: 'scale',

    campaigns: 'scale',
    /** Server-side attribution (Analytics → Atribución). */
    attribution: 'growth',

    /** More than one website: Growth brings three, Scale ten. */
    websites: 'growth',
};

/** The plan a feature asks for. */
export const planFor = (feature: TwxFeature): TwxPaidPlan => TWX_FEATURE_PLAN[feature];

/** Whether `plan` covers `required`. */
export const planCovers = (plan: TwxPlanId, required: TwxPlanId) => planRank(plan) >= planRank(required);

/**
 * Which plan a Stripe price (or a subscription) is, by its product name ("Twinny X
 * Growth"…), highest match first. Null when the name says none of them.
 */
export const planOfName = (name: string | null | undefined): TwxPaidPlan | null => {
    const n = (name ?? '').toLowerCase();
    return [...TWX_PAID_PLANS].reverse().find(p => n.includes(p)) ?? null;
};

/**
 * Which plan twinny-api's `billing_plan` is (`AppSubscription.BillingPlan`: STARTER, GROWTH,
 * SCALE… on the account's subscription and on each Stripe price). The backend's own word on
 * the plan: read before any name. Null for the ones that are not Twinny X's (CUSTOM, SKY…).
 */
export const planOfBilling = (value: string | null | undefined): TwxPaidPlan | null => {
    const v = (value ?? '').toLowerCase();
    return (TWX_PAID_PLANS as string[]).includes(v) ? (v as TwxPaidPlan) : null;
};
